import {
	createParagraphNear,
	deleteSelection,
	joinForward,
	joinBackward,
	liftEmptyBlock,
	selectNodeBackward,
	selectNodeForward,
	chainCommands,
} from "prosemirror-commands";
import { redo, undo } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import { Command, Plugin } from "prosemirror-state";
import { insertBr } from "./TextCommands";
import {
	splitListItem,
	wrapInOrderedList,
	wrapInBulletedList,
	sinkListItem,
	liftListItem,
	convertToBulletedList,
	convertToOrderedList,
} from "./ListCommands";
import {
	toggleStrong,
	toggleEm,
	toggleU,
	toggleSub,
	toggleSup,
	clearInlineFormatting,
} from "./InlineCommands";
import { splitBlockAlwaysTryClone } from "./SplitBlockExt";
import { clearBlockFormatting } from "./ClearFormattingCommands";
import { alignLeft, alignCenter, alignRight, alignJustify } from "./AlignmentCommands";
import { indent, outdent } from "./IndentCommands";
import { setHeading1, setHeading2, setHeading3 } from "./HeadingCommands";

/** Indicate that a key has been used in a binding (so default action won't happen) without actually doing anything */
function eatKey() {
	return true;
}

type EtCommand = Command;

/** Turns arrays of keybinds into single actions as required by ProseMirror, using chainCommands when there are multiple binds for a key */
function deflateBindings(bindings: { [key: string]: EtCommand[] }) {
	const ret: { [key: string]: EtCommand } = {};
	for (const k in bindings) {
		if (bindings[k].length === 1) {
			ret[k] = bindings[k][0];
		} else {
			ret[k] = chainCommands(...bindings[k]);
		}
	}
	return ret;
}

/** Generate an appropriate keymap for config, including default actions as well as any for the functions allowed in the config */
function generateKeymap(): Plugin {
	const bindings: { [key: string]: EtCommand[] } = {
		Enter: [splitListItem, createParagraphNear, liftEmptyBlock, splitBlockAlwaysTryClone],
		Backspace: [deleteSelection, joinBackward, selectNodeBackward],
		Delete: [deleteSelection, joinForward, selectNodeForward],
		"Shift-Enter": [insertBr],
		"Mod-b": [toggleStrong],
		"Mod-i": [toggleEm],
		"Mod-u": [toggleU],
		"Mod-=": [toggleSub],
		"Shift-Mod-=": [toggleSup],
		"Mod-]": [sinkListItem, indent],
		"Mod-[": [liftListItem, outdent],
		"Mod-1": [setHeading1],
		"Mod-2": [setHeading2],
		"Mod-3": [setHeading3],
		"Mod-7": [convertToOrderedList, wrapInOrderedList],
		"Mod-8": [convertToBulletedList, wrapInBulletedList],
		"Shift-Mod-l": [wrapInBulletedList],
		"Shift-Mod-n": [wrapInOrderedList],
		"Mod-l": [alignLeft],
		"Mod-e": [alignCenter],
		"Mod-r": [alignRight],
		"Mod-j": [alignJustify],
		"Mod-z": [undo],
		"Mod-y": [redo],
		"Shift-Mod-z": [redo],
		"Shift-Mod-y": [undo],
		"Mod- ": [clearInlineFormatting],
		"Mod-q": [clearBlockFormatting],
	};

	return keymap(deflateBindings(bindings));
}

export const DefaultKeymap = generateKeymap();
